import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Wordmark } from "@/components/site/Logo";
import AdminLeads from "@/components/admin/AdminLeads";
import AdminSettings from "@/components/admin/AdminSettings";
import AdminContent from "@/components/admin/AdminContent";
import AdminAccount from "@/components/admin/AdminAccount";
import AdminAnalytics from "@/components/admin/AdminAnalytics";
import { Loader2, LogOut, Inbox, Settings2, FileText, ArrowLeft, KeyRound, BarChart3 } from "lucide-react";

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await login(email, password);
    if (!res.ok) setError(res.error);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-5 noise">
      <div className="absolute inset-0 hero-grid opacity-60" />
      <form onSubmit={submit} data-testid="admin-login-form" className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-8">
        <div className="flex justify-center mb-6"><Wordmark light iconSize={38} /></div>
        <h1 className="text-center font-display text-xl font-bold text-white">Owner Login</h1>
        <p className="text-center text-slate-400 text-sm mt-1 mb-6">Manage leads, content and site settings.</p>
        <label className="text-xs text-slate-400 block mb-1.5">Email</label>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email-input"
          className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white outline-none focus:border-brand-blue mb-4" />
        <label className="text-xs text-slate-400 block mb-1.5">Password</label>
        <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password-input"
          className="w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white outline-none focus:border-brand-blue" />
        {error && <p data-testid="admin-login-error" className="mt-3 text-sm text-red-400">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-brand-blue px-6 py-3 font-semibold text-white hover:bg-blue-500 disabled:opacity-60">
          {loading ? <Loader2 size={18} className="animate-spin" /> : "Sign in"}
        </button>
        <a href="/" className="mt-5 flex items-center justify-center gap-1.5 text-sm text-slate-400 hover:text-white">
          <ArrowLeft size={14} /> Back to website
        </a>
      </form>
    </div>
  );
}

const TABS = [
  { id: "analytics", label: "Analytics", icon: BarChart3, C: AdminAnalytics },
  { id: "leads", label: "Leads", icon: Inbox, C: AdminLeads },
  { id: "content", label: "Content", icon: FileText, C: AdminContent },
  { id: "settings", label: "Settings", icon: Settings2, C: AdminSettings },
  { id: "account", label: "Account", icon: KeyRound, C: AdminAccount },
];

function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("leads");
  const Active = TABS.find((t) => t.id === tab).C;

  return (
    <div data-testid="admin-dashboard-view" className="min-h-screen bg-slate-100 flex">
      <aside className="w-64 bg-navy text-white p-6 hidden md:flex flex-col fixed inset-y-0">
        <Wordmark light iconSize={30} />
        <nav className="mt-10 space-y-1 flex-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`w-full flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                tab === t.id ? "bg-brand-blue text-white" : "text-slate-300 hover:bg-white/5"
              }`}>
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-white/10 pt-4">
          <p className="text-xs text-slate-400 truncate mb-3">{user.email}</p>
          <a href="/" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white mb-2"><ArrowLeft size={15} /> View site</a>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-10 bg-navy text-white flex items-center justify-between px-4 py-3">
        <Wordmark light iconSize={26} />
        <button onClick={logout} className="text-sm"><LogOut size={18} /></button>
      </div>
      <div className="md:hidden fixed top-[52px] inset-x-0 z-10 bg-navy flex border-t border-white/10">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 py-2.5 text-xs font-medium ${tab === t.id ? "text-white border-b-2 border-brand-blue" : "text-slate-400"}`}>{t.label}</button>
        ))}
      </div>

      <main className="flex-1 md:ml-64 p-6 sm:p-10 pt-28 md:pt-10">
        <Active />
      </main>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="animate-spin text-brand-blue" /></div>;
  if (!user) return <Login />;
  return <Dashboard />;
}
